import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { interactive } from '@/components/ui/interactive';
import { cn } from '@/lib/utils';
import type { ComponentProps } from 'react';

/**
 * The button vocabulary of the approved prototype, in shadcn/ui's shape.
 *
 * Five variants, and the prototype uses each of them for one specific job — that is why
 * they are named after the job rather than after the colour:
 *
 *   primary   pitch green, the one affirmative action on a screen
 *   secondary navy, a second affirmative action that must not compete with `primary`
 *   outline   a neutral escape hatch — cancel, close, reset
 *   ghost     header and toolbar affordances with no resting chrome
 *   danger    rose, and only for reject / delete / sign-out
 *   danger-soft  the quiet rose of the reject / close trigger beside an approve
 *   link      a text action inside a sentence or a table cell
 *
 * There is no `dir`-aware styling here on purpose: every inset is expressed with logical
 * properties, so the same class list lays out correctly under `dir="rtl"` and `dir="ltr"`.
 */
const buttonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center gap-2 rounded-lg font-bold whitespace-nowrap disabled:opacity-40 aria-disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:shrink-0',
    // No `disabled:pointer-events-none`: it hid the `not-allowed` cursor, since a
    // control that ignores the pointer never shows one (#145).
    interactive,
  ],
  {
    variants: {
      variant: {
        primary: 'bg-pitch text-white hover:bg-pitch-800',
        secondary: 'bg-navy text-white hover:bg-navy-900',
        outline: 'border border-conc-200 bg-white text-conc-600 hover:border-navy hover:text-navy',
        ghost: 'text-conc-600 hover:bg-sideline hover:text-navy',
        danger: 'bg-rose-600 text-white hover:bg-rose-700',
        /** The quiet half of a reject / close pair: rose text on a rose wash. */
        'danger-soft': 'border border-rose-200 bg-rose-50 text-rose-600 hover:border-rose-300',
        /** Inline text action — "see all", "remove photo" — with no box around it. */
        link: 'p-0 font-semibold text-pitch hover:underline',
      },
      size: {
        /** The confirm row inside an inline reason panel. */
        xs: 'rounded-md px-3 py-1 text-xs',
        sm: 'px-3.5 py-2 text-[12.5px]',
        md: 'px-4 py-2.5 text-[13px]',
        icon: 'h-8 w-8 rounded-md p-0',
        /** No padding or type size: the caller lays the control out (a tile, a link). */
        none: '',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  /** Render the child element instead of a `<button>` — used for `<Link>` actions. */
  asChild?: boolean;
}

export function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { buttonVariants };
